import { Injectable } from '@angular/core';
import {ApiService} from "../global/api.service";
import {Product, Recipe} from "../../types/models";
import {ProductService} from './product.service';
import {RecipeService} from './recipe.service';
import {Observable} from "rxjs";
import {map} from "rxjs/operators";

export interface SearchFilter {
  Diets: string[]; // all tags have to apply
  Xxl: boolean; // private customers: large packs are shown as well
  BusinessOnly: boolean; // corporate customers: only large packs
}

@Injectable({
  providedIn: 'root'
})
export class SearchService {

  recommendedFilterCached: string[];

  static readonly FILTER_KEY = 'kiwi-search-filter';
  // classification of the products (product.diet.service.js of the api)
  static readonly DIETS = [
    { Key: 'vegan', Name: 'Vegan' },
    { Key: 'vegetarisch', Name: 'Vegetarisch' },
    { Key: 'glutenfrei', Name: 'Glutenfrei' },
    { Key: 'laktosefrei', Name: 'Laktosefrei' },
    { Key: 'eifrei', Name: 'Eifrei' },
    { Key: 'nussfrei', Name: 'Nussfrei' },
    { Key: 'sojafrei', Name: 'Sojafrei' }
  ];

  constructor(
    private api: ApiService,
    private productService: ProductService,
    private recipeService: RecipeService
  ) { }

  public getRecommendedFilterCached(): string[] {
    return this.recommendedFilterCached;
  }

  public async getRecommendedFilter(): Promise<string[]> {
    const filter = await this.api.get('search/Names');
    this.recommendedFilterCached = filter;
    return filter;
  }

  public async getSearchResult(Name: string = ''): Promise<{ Products?: Product[], Recipes?: Recipe[]}> {
    const searchResult = await this.api.get(`search/${Name}`);
    const recipes = this.recipeService.sortRecipesByAvailability(searchResult?.Recipes);
    (recipes || []).forEach(recipe => this.recipeService.setPrice(recipe));
    return {
      Recipes: recipes,
      Products: this.productService.sortProductsByAvailability(searchResult?.Products)
    }
  }

  // wie getSearchResult(), aber abbrechbar: unsubscribe() stoppt die noch laufende Anfrage wirklich
  // filter of the customer search, kept in the local storage of the device
  public getFilter(): SearchFilter {
    try {
      const stored = JSON.parse(localStorage.getItem(SearchService.FILTER_KEY) || '{}') || {};
      return {
        Diets: Array.isArray(stored.Diets) ? stored.Diets.filter((diet: string) => SearchService.DIETS.some(option => option.Key === diet)) : [],
        Xxl: stored.Xxl === true,
        BusinessOnly: stored.BusinessOnly === true
      };
    } catch (e) {
      return { Diets: [], Xxl: false, BusinessOnly: false };
    }
  }

  public saveFilter(filter: SearchFilter) {
    try {
      localStorage.setItem(SearchService.FILTER_KEY, JSON.stringify(filter));
    } catch (e) {
    }
  }

  public getSearchResultCancelable(Name: string = '', filter?: SearchFilter): Observable<{ Products?: Product[], Recipes?: Recipe[] }> {
    const params: any = {};
    if (filter?.Diets?.length) {
      params.Diets = filter.Diets.join(',');
    }
    if (filter?.Xxl) {
      params.Xxl = true;
    }
    if (filter?.BusinessOnly) {
      params.BusinessOnly = true;
    }
    return this.api.getCancelable(`search/${Name}`, params).pipe(
      map(searchResult => {
        const recipes = this.recipeService.sortRecipesByAvailability(searchResult?.Recipes);
        (recipes || []).forEach(recipe => this.recipeService.setPrice(recipe));
        return {
          Recipes: recipes,
          Products: this.productService.sortProductsByAvailability(searchResult?.Products)
        };
      })
    );
  }
}
